import { useEffect, useState } from "react";
import { platformClient } from "./platformApi";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORY_META = {
    grondstof: { label: "Grondstof", color: "#c9a227" },
    hulpstof: { label: "Hulpstof", color: "#8a8578" },
    verpakking: { label: "Verpakking", color: "#5c584e" },
    overig: { label: "Overig materiaal", color: "#3a382f" },
    arbeid: { label: "Arbeid", color: "#f5f0e6" },
    machine: { label: "Machine", color: "#a3852a" },
    energie: { label: "Energie", color: "#d4b04a" },
    overhead: { label: "Overhead", color: "#4a463c" },
};

const LABEL = "text-[10px] uppercase tracking-[0.25em] text-ash";

export const CostAnalysisTab = () => {
    const [data, setData] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await platformClient.get("/production/cost-analysis");
                setData(data);
            } catch {}
        })();
    }, []);

    if (!data) {
        return <p data-testid="cost-analysis-loading" className="mt-10 text-xs uppercase tracking-[0.3em] text-ash">Kostenanalyse laden…</p>;
    }

    const cats = Object.entries(data.categories)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1]);
    const catTotal = cats.reduce((s, [, v]) => s + v, 0) || 1;

    return (
        <div data-testid="cost-analysis-tab" className="mt-8">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                <div className="border border-white/10 bg-coal p-6 lg:col-span-2">
                    <p className={LABEL}>Kostenopbouw per categorie (alle recepturen, per batch)</p>
                    {cats.length === 0 ? (
                        <p className="mt-4 text-sm text-ash">Nog geen recepturen met kosten.</p>
                    ) : (
                        <div className="mt-5 space-y-4">
                            {cats.map(([key, value]) => {
                                const meta = CATEGORY_META[key] || { label: key, color: "#8a8578" };
                                return (
                                    <div key={key} data-testid={`cost-cat-${key}`}>
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-ash">{meta.label}</span>
                                            <span className="text-bone">
                                                {euro(value)} <span className="text-ash/60">({Math.round((value / catTotal) * 100)}%)</span>
                                            </span>
                                        </div>
                                        <div className="mt-1.5 h-2 bg-white/10">
                                            <div
                                                className="h-full transition-all duration-700"
                                                style={{ width: `${(value / catTotal) * 100}%`, backgroundColor: meta.color }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                            <p className="pt-2 text-right text-xs text-ash">
                                Totaal per batch: <span className="text-gold">{euro(catTotal)}</span>
                            </p>
                        </div>
                    )}
                    <p className="mt-6 border-t border-white/10 pt-4 text-[11px] leading-relaxed text-ash/70">
                        Overheadtarief: {euro(data.overhead_rate)}/uur — berekend uit je vaste lasten en productieve uren bij Kosten &amp; tarieven.
                    </p>
                </div>

                <div className="border border-white/10 bg-coal p-6 lg:col-span-3">
                    <p className={LABEL}>Kostprijs en adviesprijs per product</p>
                    <div className="mt-5 space-y-5">
                        {data.products.map((p) => {
                            const parts = Object.entries(p.parts).filter(([, v]) => v > 0);
                            return (
                                <div key={p.id} data-testid={`cost-product-${p.id}`}>
                                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                                        <p className="text-sm text-bone">{p.name}</p>
                                        <p className="text-xs text-ash">
                                            kostprijs <span className="text-bone">{euro(p.cost_per_unit)}</span>/eenh.
                                            {" "}→ advies <span className="text-gold">{euro(p.price_per_unit)}</span>/eenh.
                                            {" "}· marge {p.margin_perc}%
                                        </p>
                                    </div>
                                    <div className="mt-2 flex h-3 w-full overflow-hidden bg-white/5">
                                        {parts.map(([key, value]) => {
                                            const meta = CATEGORY_META[key] || { label: key, color: "#8a8578" };
                                            return (
                                                <div
                                                    key={key}
                                                    title={`${meta.label}: ${euro(value)}`}
                                                    className="h-full"
                                                    style={{ width: `${(value / (p.total_cost || 1)) * 100}%`, backgroundColor: meta.color }}
                                                />
                                            );
                                        })}
                                    </div>
                                    <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-ash/60">
                                        batch {euro(p.total_cost)} · opbrengst {p.batch_yield} eenheden
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-6 flex flex-wrap gap-3 border-t border-white/10 pt-4">
                        {Object.entries(CATEGORY_META).map(([key, meta]) => (
                            <span key={key} className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-ash">
                                <span className="inline-block h-2 w-2" style={{ backgroundColor: meta.color }} />
                                {meta.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
